import { FC, ReactNode, useEffect, useRef, useState } from "react";
import {
	Box,
	Button,
	Chip,
	LinearProgress,
	MenuItem,
	OutlinedInput,
	Paper,
	Select,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useUserStore } from "../../../../../stores/user.store";
import {
	useCurrencySetting,
	useSettings,
} from "../../../../../hooks/useSetting";
import useProgramActivitySummary from "../../../../../hooks/useProgramActivitySummary";
import { useActivePayers } from "../../../../../hooks/userPayers";
import { useGroupLabels } from "../../../../../hooks/groupLabels";
import { useGetProviderSitesByOUId } from "../../../../../hooks/useBeneficiaries";
import { useAppTheme } from "../../../../../utils/theme";
import {
	NoProgramCard,
	ProgramFilterField,
	ReportTabProps,
	groupLabelsForSites,
	payersForProgram,
	programNameOf,
	programOrgUnitIds,
} from "../common";

interface Option {
	id: string;
	name: string | null;
}

const toOptions = (
	list: ReadonlyArray<{ _id?: string; id?: string; name?: string | null }>,
): Option[] =>
	(list ?? []).map((o) => ({ id: o._id ?? o.id ?? "", name: o.name ?? null }));

const ProgramActivitySummaryTab: FC<ReportTabProps> = ({
	programId,
	programs,
	onProgramChange,
}) => {
	const theme = useAppTheme();
	const { userInfo } = useUserStore();
	const accountId = userInfo?.accountId ?? "";
	const currencyMask = useCurrencySetting()?.mask ?? "$";
	const programName = programNameOf(programs, programId);

	const [orgUnitIds, setOrgUnitIds] = useState<string[]>([]);
	const [payerIds, setPayerIds] = useState<string[]>([]);
	const [groupLabelIds, setGroupLabelIds] = useState<string[]>([]);
	const [providerId, setProviderId] = useState<string>("");
	const [startDate, setStartDate] = useState<string>("");
	const [endDate, setEndDate] = useState<string>("");

	// SIMS-7396 / SIMS-7397: payers and group labels are program-specific — clear
	// the selection when the program changes so a stale value isn't sent for a
	// different program.
	useEffect(() => {
		setPayerIds([]);
		setGroupLabelIds([]);
	}, [programId]);

	const { data, isLoading, isFetching } = useProgramActivitySummary({
		accountId,
		programId,
		orgUnitIds: orgUnitIds.length ? orgUnitIds.join(",") : undefined,
		payerIds: payerIds.length ? payerIds.join(",") : undefined,
		groupLabelIds: groupLabelIds.length ? groupLabelIds.join(",") : undefined,
		providerId: providerId || undefined,
		startDate: startDate || undefined,
		endDate: endDate || undefined,
	});

	const { data: payers } = useActivePayers();
	const { data: groupLabels } = useGroupLabels();
	// SIMS-7397: group labels are scoped to the OUs the user has for the selected
	// program (same logic as the Beneficiary Report): resolve the program's OUs,
	// fetch their provider sites, then keep the labels attached to those sites.
	const groupLabelOuIds = programOrgUnitIds(userInfo?.programs, programId);
	const { data: providerSites } = useGetProviderSitesByOUId({
		ouIds: groupLabelOuIds,
		queryKey: "sims-reports-provider-sites",
	});

	const siteOptions = toOptions(data?.availableSites ?? []);
	const providerOptions = toOptions(data?.availableProviders ?? []);
	// SIMS-7396: the Payer filter lists only payers configured for the selected
	// program (program.payers), intersected with the account's active payers.
	// When the program has none, the dropdown is disabled.
	const selectedProgram = programs.find((p) => p._id === programId);
	const payerOptions = toOptions(payersForProgram(payers, selectedProgram));
	const payersDisabled = payerOptions.length === 0;
	const groupLabelOptions = toOptions(
		groupLabelsForSites(groupLabels, providerSites),
	);

	const money = (n: number): string => `${currencyMask}${(n ?? 0).toFixed(2)}`;
	const counts = data?.enrollmentCounts;
	const inc = data?.incentiveSummary;
	const plural = data?.labels.plural ?? "Members";

	// Brand logo comes from Settings → brand information; SIMS product logo is a
	// static asset. Generated date stamps the report (matches the handoff).
	const { data: settings } = useSettings();
	const brand = settings?.brandInformation;
	const generated = dayjs().format("MM/DD/YYYY");

	// Design tokens → theme palette (driven by the account's themeColors).
	const c = {
		cardBg: theme.palette.surface.light, // #fff
		band: theme.palette.surface.main, // tinted band
		border: theme.palette.outline.light,
		divider: theme.palette.outline.light,
		label: theme.palette.textSecondary.main,
		muted: theme.palette.textSecondary.main,
		ink: theme.palette.textPrimary.main,
		good: theme.palette.secondary.dark,
		bad: theme.palette.error.main,
		warn: theme.palette.warning.dark,
		money: theme.palette.primary.main,
		bandDark: theme.palette.primary.dark, // --sims-purple-900 (title band)
		onDark: theme.palette.surface.light, // text on the band
		teal: theme.palette.secondary.main, // --sims-teal-500 (section rule)
	};

	const nameById = (opts: Option[], ids: string[]): string =>
		ids
			.map((id) => opts.find((o) => o.id === id)?.name ?? id)
			.filter(Boolean)
			.join(", ");

	// All filters by name, with values (or "All"/"Any" when unselected) — shown
	// in the report header so the PDF records exactly what was applied.
	const filterPairs = (): Array<[string, string]> => [
		["Program", programName || "All"],
		["Org units", nameById(siteOptions, orgUnitIds) || "All"],
		["Payer", nameById(payerOptions, payerIds) || "All"],
		["Group labels", nameById(groupLabelOptions, groupLabelIds) || "All"],
		[
			"Provider",
			providerOptions.find((o) => o.id === providerId)?.name || "All",
		],
		["Start date", startDate ? dayjs(startDate).format("MM/DD/YYYY") : "Any"],
		["End date", endDate ? dayjs(endDate).format("MM/DD/YYYY") : "Any"],
	];

	const bucketColor = (key: string, label: string): string => {
		const s = `${key} ${label}`.toLowerCase();
		if (/(unexcused|positive|missed|absent)/.test(s)) return c.bad;
		if (/excus/.test(s)) return c.warn;
		if (/(negative|attend)/.test(s)) return c.good;
		return c.ink;
	};

	// Redesign (A2) — the PDF is the on-screen report captured as-is (brand band,
	// KPI band, donut + results table) so it matches the screen exactly. The
	// filter bar and buttons are excluded via data-html2canvas-ignore.
	const reportRef = useRef<HTMLDivElement>(null);

	const clearFilters = (): void => {
		// SIMS-7390: a program is always selected — reset to the first assigned
		// program rather than an empty selection.
		onProgramChange(programs[0]?._id ?? "");
		setOrgUnitIds([]);
		setPayerIds([]);
		setGroupLabelIds([]);
		setProviderId("");
		setStartDate("");
		setEndDate("");
	};

	const handlePdf = async (): Promise<void> => {
		const el = reportRef.current;
		if (!el) return;
		const canvas = await html2canvas(el, {
			scale: 2,
			useCORS: true,
			backgroundColor: theme.palette.surface.light,
		});
		const img = canvas.toDataURL("image/png");
		const pdf = new jsPDF("portrait", "pt", "a4");
		const pageW = pdf.internal.pageSize.getWidth();
		const pageH = pdf.internal.pageSize.getHeight();
		const imgW = pageW;
		const imgH = (canvas.height * imgW) / canvas.width;
		let heightLeft = imgH;
		let position = 0;
		pdf.addImage(img, "PNG", 0, position, imgW, imgH);
		heightLeft -= pageH;
		while (heightLeft > 0) {
			position -= pageH;
			pdf.addPage();
			pdf.addImage(img, "PNG", 0, position, imgW, imgH);
			heightLeft -= pageH;
		}
		pdf.save("program_summary.pdf");
	};

	// ---- presentational helpers -------------------------------------------
	const FilterField: FC<{
		label: string;
		multi?: boolean;
		children: ReactNode;
	}> = ({ label, multi, children }) => (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
			<Stack direction="row" spacing={1} alignItems="center">
				<Typography
					sx={{
						fontSize: 12,
						fontWeight: 600,
						letterSpacing: "0.06em",
						color: c.label,
					}}
				>
					{label.toUpperCase()}
				</Typography>
				{multi && (
					<Chip
						size="small"
						label="MULTI"
						sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
						color="primary"
						variant="outlined"
					/>
				)}
			</Stack>
			{children}
		</Box>
	);

	const multiInput = (
		value: string[],
		options: Option[],
		onChange: (v: string[]) => void,
		placeholder: string,
		disabled = false,
	): ReactNode => (
		<Select
			size="small"
			multiple
			displayEmpty
			disabled={disabled}
			value={value}
			input={<OutlinedInput />}
			onChange={(e) => {
				const v = e.target.value;
				onChange(typeof v === "string" ? v.split(",") : v);
			}}
			renderValue={(sel) =>
				(sel as string[]).length === 0 ? (
					<Typography sx={{ color: c.muted }}>{placeholder}</Typography>
				) : (
					<Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
						{(sel as string[]).map((id) => (
							<Chip
								key={id}
								size="small"
								label={options.find((o) => o.id === id)?.name ?? id}
							/>
						))}
					</Box>
				)
			}
		>
			{options.map((o) => (
				<MenuItem key={o.id} value={o.id}>
					{o.name ?? o.id}
				</MenuItem>
			))}
		</Select>
	);

	// ---- report chrome (A2) — theme tokens + brand logo from settings -------
	const SectionHead: FC<{ title: string; sub?: string }> = ({ title, sub }) => (
		<Box
			sx={{
				display: "flex",
				alignItems: "baseline",
				justifyContent: "space-between",
				borderBottom: `2px solid ${c.teal}`,
				pb: 0.75,
				mb: 2,
				gap: 2,
			}}
		>
			<Typography sx={{ fontSize: 16, fontWeight: 700, color: c.bandDark }}>
				{title}
			</Typography>
			{sub && (
				<Typography sx={{ fontSize: 12, color: c.muted }}>{sub}</Typography>
			)}
		</Box>
	);

	const KpiCard: FC<{ label: string; value: ReactNode; color?: string }> = ({
		label,
		value,
		color,
	}) => (
		<Box
			sx={{
				border: `1px solid ${c.border}`,
				borderRadius: 2,
				bgcolor: c.cardBg,
				p: 1.75,
			}}
		>
			<Typography
				sx={{ fontSize: 12, color: c.muted, lineHeight: 1.3, minHeight: 32 }}
			>
				{label}
			</Typography>
			<Typography
				sx={{ fontSize: 26, fontWeight: 600, color: color ?? c.ink, mt: 0.5 }}
			>
				{value}
			</Typography>
		</Box>
	);

	const EventResults: FC<{
		eventName: string;
		buckets: Array<{ key: string; label: string; count: number }>;
	}> = ({ eventName, buckets }) => {
		const total = buckets.reduce((s, b) => s + (b.count ?? 0), 0);
		const donut = buckets.map((b) => ({
			name: b.label,
			value: b.count ?? 0,
			color: bucketColor(b.key, b.label),
		}));
		return (
			<Box sx={{ mt: 3 }}>
				<SectionHead
					title="Visit results"
					sub={`${eventName} · ${total} total`}
				/>
				<Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 2,
							border: `1px solid ${c.border}`,
							borderRadius: 2,
							bgcolor: c.band,
							p: 2,
							minWidth: 300,
						}}
					>
						<Box sx={{ width: 150, height: 150, position: "relative" }}>
							<ResponsiveContainer width="100%" height="100%">
								<PieChart>
									<Pie
										data={donut}
										dataKey="value"
										nameKey="name"
										innerRadius={46}
										outerRadius={72}
										paddingAngle={1}
										stroke={c.cardBg}
									>
										{donut.map((d) => (
											<Cell key={d.name} fill={d.color} />
										))}
									</Pie>
								</PieChart>
							</ResponsiveContainer>
							<Box
								sx={{
									position: "absolute",
									inset: 0,
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<Typography
									sx={{
										fontSize: 22,
										fontWeight: 600,
										color: c.ink,
										lineHeight: 1,
									}}
								>
									{total}
								</Typography>
								<Typography sx={{ fontSize: 10, color: c.muted }}>
									total visits
								</Typography>
							</Box>
						</Box>
						<Stack spacing={1} sx={{ flex: 1, minWidth: 120 }}>
							{donut.map((d) => (
								<Stack
									key={d.name}
									direction="row"
									alignItems="center"
									spacing={1}
								>
									<Box
										sx={{
											width: 10,
											height: 10,
											borderRadius: 0.5,
											bgcolor: d.color,
											flex: "none",
										}}
									/>
									<Typography sx={{ fontSize: 12, color: c.ink, flex: 1 }}>
										{d.name}
									</Typography>
									<Typography sx={{ fontSize: 12, color: c.muted }}>
										{d.value}
									</Typography>
								</Stack>
							))}
						</Stack>
					</Box>

					<Box
						sx={{
							flex: 1,
							minWidth: 260,
							border: `1px solid ${c.border}`,
							borderRadius: 2,
							overflow: "hidden",
						}}
					>
						<Box
							component="table"
							sx={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
						>
							<Box component="thead">
								<Box component="tr" sx={{ bgcolor: c.band }}>
									{["Result", "Count", "% of total"].map((h, i) => (
										<Box
											component="th"
											key={h}
											sx={{
												textAlign: i === 0 ? "left" : "right",
												p: "8px 12px",
												fontSize: 11,
												fontWeight: 700,
												color: c.bandDark,
												borderBottom: `1px solid ${c.border}`,
											}}
										>
											{h}
										</Box>
									))}
								</Box>
							</Box>
							<Box component="tbody">
								{buckets.map((b) => (
									<Box component="tr" key={b.key}>
										<Box
											component="td"
											sx={{
												p: "8px 12px",
												borderTop: `1px solid ${c.border}`,
											}}
										>
											<Box
												component="span"
												sx={{
													display: "inline-flex",
													alignItems: "center",
													gap: 0.75,
													fontSize: 12,
													fontWeight: 700,
													color: bucketColor(b.key, b.label),
												}}
											>
												<Box
													sx={{
														width: 7,
														height: 7,
														borderRadius: "50%",
														bgcolor: bucketColor(b.key, b.label),
													}}
												/>
												{b.label}
											</Box>
										</Box>
										<Box
											component="td"
											sx={{
												p: "8px 12px",
												textAlign: "right",
												borderTop: `1px solid ${c.border}`,
											}}
										>
											{b.count}
										</Box>
										<Box
											component="td"
											sx={{
												p: "8px 12px",
												textAlign: "right",
												borderTop: `1px solid ${c.border}`,
												color: c.muted,
											}}
										>
											{total ? ((b.count / total) * 100).toFixed(2) : "0.00"}%
										</Box>
									</Box>
								))}
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>
		);
	};

	const ReportHeader: FC = () => (
		<Box>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				sx={{ mb: 2 }}
			>
				{brand?.logo ? (
					<Box
						component="img"
						src={brand.logo}
						alt={brand.title ?? "Brand"}
						sx={{ height: 40, maxWidth: 220, objectFit: "contain" }}
					/>
				) : (
					<Typography sx={{ fontSize: 18, fontWeight: 700, color: c.ink }}>
						{brand?.title ?? ""}
					</Typography>
				)}
				<Box
					component="img"
					src="/assets/sims-logo.png"
					alt="SIMS"
					sx={{ height: 44, objectFit: "contain" }}
				/>
			</Stack>
			<Box
				sx={{
					bgcolor: c.bandDark,
					borderRadius: 2,
					px: 3,
					py: 2,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 2,
					flexWrap: "wrap",
				}}
			>
				<Typography sx={{ color: c.onDark, fontSize: 22, fontWeight: 700 }}>
					Program Summary
				</Typography>
				<Stack direction="row" spacing={2} alignItems="center">
					<Typography sx={{ color: c.onDark, opacity: 0.75, fontSize: 12 }}>
						Generated {generated}
					</Typography>
					<Button
						variant="contained"
						color="secondary"
						size="small"
						startIcon={<FileDownloadOutlinedIcon />}
						onClick={handlePdf}
						data-html2canvas-ignore="true"
					>
						Export to PDF
					</Button>
				</Stack>
			</Box>
			<Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1.5 }}>
				{filterPairs().map(([k, v]) => (
					<Typography key={k} sx={{ fontSize: 11, color: c.muted }}>
						{k}:{" "}
						<Box component="span" sx={{ color: c.ink, fontWeight: 600 }}>
							{v}
						</Box>
					</Typography>
				))}
			</Box>
		</Box>
	);

	return (
		<Stack spacing={3} ref={reportRef}>
			{/* Report header: brand logo + SIMS, purple title band, context line */}
			<Paper
				variant="outlined"
				sx={{ borderColor: c.border, borderRadius: 3, p: 3 }}
			>
				<ReportHeader />
			</Paper>

			{/* Filter bar card (excluded from the PDF capture) */}
			<Paper
				data-html2canvas-ignore="true"
				variant="outlined"
				sx={{ borderColor: c.border, borderRadius: 3, p: 3 }}
			>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: {
							xs: "1fr",
							sm: "repeat(2,1fr)",
							md: "repeat(4,1fr) 112px",
						},
						gap: 3,
						alignItems: "end",
					}}
				>
					<ProgramFilterField
						programId={programId}
						programs={programs}
						onProgramChange={onProgramChange}
						labelColor={c.label}
						mutedColor={c.muted}
						errorColor={theme.palette.error.main}
					/>
					<FilterField label="Org units">
						{multiInput(orgUnitIds, siteOptions, setOrgUnitIds, "All sites")}
					</FilterField>
					<FilterField label="Payer" multi>
						{multiInput(
							payerIds,
							payerOptions,
							setPayerIds,
							payersDisabled ? "No payers for this program" : "All payers",
							payersDisabled,
						)}
					</FilterField>
					<FilterField label="Group labels">
						{multiInput(
							groupLabelIds,
							groupLabelOptions,
							setGroupLabelIds,
							"All",
						)}
					</FilterField>
					<FilterField label="Provider">
						<Select
							size="small"
							displayEmpty
							value={providerId}
							input={<OutlinedInput />}
							onChange={(e) => setProviderId(e.target.value)}
							renderValue={(v) =>
								v ? (
									(providerOptions.find((o) => o.id === v)?.name ?? v)
								) : (
									<Typography sx={{ color: c.muted }}>All providers</Typography>
								)
							}
						>
							<MenuItem value="">All providers</MenuItem>
							{providerOptions.map((o) => (
								<MenuItem key={o.id} value={o.id}>
									{o.name ?? o.id}
								</MenuItem>
							))}
						</Select>
					</FilterField>
					<FilterField label="Start date">
						<TextField
							size="small"
							type="date"
							value={startDate}
							onChange={(e) => setStartDate(e.target.value)}
							InputLabelProps={{ shrink: true }}
						/>
					</FilterField>
					<FilterField label="End date">
						<TextField
							size="small"
							type="date"
							value={endDate}
							onChange={(e) => setEndDate(e.target.value)}
							InputLabelProps={{ shrink: true }}
						/>
					</FilterField>
					{/* Clear all — 5th column spanning both filter rows (per handoff) */}
					<Button
						variant="contained"
						onClick={clearFilters}
						sx={{
							gridColumn: { xs: "1 / -1", md: "5" },
							gridRow: { md: "1 / span 2" },
							alignSelf: "stretch",
							minHeight: 44,
							bgcolor: c.bandDark,
							color: c.onDark,
							fontWeight: 700,
							letterSpacing: "0.02em",
							"&:hover": { bgcolor: c.bandDark, opacity: 0.92 },
						}}
					>
						Clear all
					</Button>
				</Box>
			</Paper>

			{/* Reserve the bar's height so the content doesn't jump on refetch. */}
			<Box data-html2canvas-ignore="true" sx={{ height: 4 }}>
				{(isLoading || isFetching) && <LinearProgress />}
			</Box>

			{!programId && (
				<NoProgramCard borderColor={c.border} mutedColor={c.muted} />
			)}

			{programId && (
				<Paper
					variant="outlined"
					sx={{ borderColor: c.border, borderRadius: 3, p: 3 }}
				>
					{/* Program at a glance — merged enrollment + incentive KPI band */}
					<SectionHead title="Program at a glance" />
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: {
								xs: "repeat(2,1fr)",
								md: "repeat(4,1fr)",
							},
							gap: 1.5,
						}}
					>
						<KpiCard
							label={`${plural} enrolled`}
							value={counts?.enrolled ?? 0}
						/>
						<KpiCard
							label={`Active ${plural.toLowerCase()}`}
							value={counts?.active ?? 0}
						/>
						<KpiCard
							label="Completed the program"
							value={counts?.completed ?? 0}
						/>
						<KpiCard
							label={`${plural} dis-enrolled`}
							value={counts?.disenrolled ?? 0}
						/>
						<KpiCard
							label="Total earned"
							value={money(inc?.totalEarned ?? 0)}
							color={c.money}
						/>
						<KpiCard
							label="Earned transactions"
							value={inc?.earnedTxnCount ?? 0}
						/>
						<KpiCard
							label="Total disbursed"
							value={money(inc?.totalDisbursed ?? 0)}
							color={c.money}
						/>
						<KpiCard
							label="Disbursed transactions"
							value={inc?.disbursedTxnCount ?? 0}
						/>
					</Box>

					{/* Visit results — donut (result mix) + legend + results table */}
					{(data?.visitResultsByEvent ?? []).length === 0 ? (
						<Box sx={{ mt: 3 }}>
							<SectionHead title="Visit results" />
							<Typography sx={{ color: c.muted }}>
								No visit results for the selected filters.
							</Typography>
						</Box>
					) : (
						(data?.visitResultsByEvent ?? []).map((ev) => (
							<EventResults
								key={ev.eventId}
								eventName={ev.eventName ?? "Event"}
								buckets={ev.buckets}
							/>
						))
					)}

					{/* Footer */}
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="center"
						sx={{ mt: 4, pt: 1.5, borderTop: `1px solid ${c.border}` }}
					>
						<Typography sx={{ fontSize: 11, color: c.muted }}>
							{[programName, brand?.title].filter(Boolean).join(" — ")}
						</Typography>
						<Typography
							sx={{
								fontSize: 11,
								fontWeight: 700,
								color: c.bandDark,
								letterSpacing: "0.08em",
							}}
						>
							SIMS
						</Typography>
						<Typography sx={{ fontSize: 11, color: c.muted }}>
							Generated {generated}
						</Typography>
					</Stack>
				</Paper>
			)}
		</Stack>
	);
};

export default ProgramActivitySummaryTab;
